import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { User, Loader2, TrendingUp, ShoppingBag } from 'lucide-react';

import ConsentRow from './auth/ConsentRow';
import AuthFormFields from './auth/AuthFormFields';
import ForgotPasswordView from './auth/ForgotPasswordView';
import TwoFactorView from './auth/TwoFactorView';
import EmailOtpView from './auth/EmailOtpView';
import RegistrationSuccessView from './auth/RegistrationSuccessView';
import { activateUserSession, clearUserSessionActivity, isUserSessionIdle } from '../utils/sessionActivity';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Auth orchestrator.
 * Owns shared state and submission logic; delegates presentation to:
 *   - AuthFormFields   (email/password/phone form)
 *   - ConsentRow       (NDPA/GDPR legal consent checkbox)
 *   - ForgotPasswordView (self-contained alternate view)
 *   - TwoFactorView    (admin 2FA TOTP flow)
 */
const Auth = () => {
  const [searchParams] = useSearchParams();
  const initialType = searchParams.get('type') === 'investor' ? 'investor' : 'customer';

  const [accountType, setAccountType] = useState(initialType);
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [show2FA, setShow2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [emailOtpChallenge, setEmailOtpChallenge] = useState(null);
  const [legalConsent, setLegalConsent] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [sessionExpired, setSessionExpired] = useState(false);

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    referral_code: searchParams.get('ref') || '',
  });
  const [dialCode, setDialCode] = useState('+234');

  const navigate = useNavigate();

  useEffect(() => {
    if (sessionStorage.getItem('dfl_session_expired') === '1') {
      sessionStorage.removeItem('dfl_session_expired');
      setSessionExpired(true);
      toast.info('You were signed out after 10 minutes of inactivity. Please sign in again.');
    }
  }, []);

  // Redirect already-authenticated users
  useEffect(() => {
    const userData = localStorage.getItem('user_data');
    const investorData = localStorage.getItem('investor_data');
    if ((userData || investorData) && isUserSessionIdle()) {
      localStorage.removeItem('user_data');
      localStorage.removeItem('investor_data');
      clearUserSessionActivity();
      sessionStorage.setItem('dfl_session_expired', '1');
      setSessionExpired(true);
      return;
    }
    if (userData) navigate('/account');
    else if (investorData) navigate('/investor/dashboard');
  }, [navigate]);

  // Sync account type from URL param
  useEffect(() => {
    const typeParam = searchParams.get('type');
    if (typeParam === 'investor') setAccountType('investor');
  }, [searchParams]);

  // Reset legal consent whenever the user switches between Sign In and Sign Up
  useEffect(() => {
    setLegalConsent(false);
  }, [isLogin, accountType]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      setFormData((prev) => ({ ...prev, phone: value.replace(/\D/g, '') }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const isValidPhoneNumber = (phone) => {
    const digits = phone.replace(/\D/g, '');
    return digits.length >= 6 && digits.length <= 15;
  };

  const getFullPhone = () => `${dialCode}${formData.phone}`;

  const logAuthConsent = (source) => {
    try {
      axios.post(
        `${API}/consent/log`,
        { event_type: 'auth_consent', source, email: formData.email || undefined },
        { withCredentials: true }
      ).catch((err) => console.warn('consent log failed:', err?.message || err));
    } catch (e) { console.warn('consent log error:', e); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!legalConsent) {
      toast.error('Please accept the Terms & Conditions and Privacy Policy to continue');
      return;
    }

    if (!isLogin) {
      if (!formData.full_name.trim()) { toast.error('Full name is required'); return; }
      if (!formData.phone.trim()) { toast.error('Phone number is required'); return; }
      if (!isValidPhoneNumber(formData.phone)) { toast.error('Please enter a valid phone number'); return; }
    }
    if (!formData.email.trim()) { toast.error('Email is required'); return; }
    if (formData.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (!isLogin && formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      let endpoint;
      let payload;

      if (isLogin) {
        endpoint = '/auth/login';
        const deviceToken = localStorage.getItem('dfl_device_token') || undefined;
        payload = { email: formData.email, password: formData.password, device_token: deviceToken };
      } else if (accountType === 'investor') {
        endpoint = '/auth/investor/register';
        payload = { email: formData.email, password: formData.password, full_name: formData.full_name, phone: getFullPhone(), referral_code: formData.referral_code };
      } else {
        endpoint = '/auth/register';
        payload = { email: formData.email, password: formData.password, full_name: formData.full_name, phone: getFullPhone(), referral_code: formData.referral_code };
      }

      const response = await axios.post(`${API}${endpoint}`, payload, { withCredentials: true });

      if (response.data.success) {
        logAuthConsent(isLogin ? 'auth_signin' : 'auth_signup');

        if (response.data.requires_2fa) {
          setTempToken(response.data.temp_token);
          setShow2FA(true);
          toast.info('Enter your 2FA code from your authenticator app');
          setLoading(false);
          return;
        }

        // Email-OTP 2FA (Hybrid flow #5) — required when device isn't trusted.
        if (response.data.requires_email_otp) {
          setEmailOtpChallenge({
            temp_token: response.data.temp_token,
            user_type: response.data.user_type,
            email_masked: response.data.email_masked,
            expires_in_seconds: response.data.expires_in_seconds,
            // Remember the email locally so we route correctly after verify.
            email: formData.email,
          });
          toast.info('We sent a 6-digit code to your email');
          setLoading(false);
          return;
        }

        if (response.data.is_admin) {
          toast.success(`Welcome, ${response.data.user?.full_name || 'Admin'}!`);
          navigate('/admin/dashboard');
          return;
        }

        // New signup → show "check your email" screen instead of auto-routing to dashboard.
        if (!isLogin) {
          setRegisteredEmail(formData.email);
          toast.success('Account created — please verify your email');
          setLoading(false);
          return;
        }

        // Login: persist user (with verification flag) and route.
        const verifiedFlag = !!(response.data.is_email_verified ?? response.data.user?.is_email_verified);
        const userPayload = { ...(response.data.user || {}), is_email_verified: verifiedFlag };

        if (response.data.user_type === 'investor') {
          localStorage.setItem('investor_data', JSON.stringify(userPayload));
          activateUserSession();
          toast.success('Welcome back, Investor!');
          navigate('/investor/dashboard');
          return;
        }

        localStorage.setItem('user_data', JSON.stringify(userPayload));
        activateUserSession();
        toast.success('Welcome back!');
        navigate('/account');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  // Alternate views
  if (registeredEmail) {
    return (
      <RegistrationSuccessView
        email={registeredEmail}
        accountType={accountType}
        onBackToSignIn={() => {
          setRegisteredEmail('');
          setIsLogin(true);
          setFormData((prev) => ({ ...prev, password: '', confirmPassword: '' }));
        }}
      />
    );
  }
  if (showForgotPassword) {
    return <ForgotPasswordView onBack={() => setShowForgotPassword(false)} />;
  }
  if (show2FA) {
    return (
      <TwoFactorView
        tempToken={tempToken}
        onBack={() => { setShow2FA(false); setTempToken(''); }}
      />
    );
  }
  if (emailOtpChallenge) {
    return (
      <EmailOtpView
        challenge={emailOtpChallenge}
        onBack={() => setEmailOtpChallenge(null)}
        onComplete={(data) => {
          // data shape mirrors /auth/login (token, user, user_type, is_admin, is_email_verified, device_token)
          if (data.is_admin) {
            toast.success(`Welcome, ${data.user?.full_name || 'Admin'}!`);
            navigate('/admin/dashboard');
            return;
          }
          const verifiedFlag = !!(data.is_email_verified ?? data.user?.is_email_verified);
          const userPayload = { ...(data.user || {}), is_email_verified: verifiedFlag };
          if (data.user_type === 'investor') {
            localStorage.setItem('investor_data', JSON.stringify(userPayload));
            activateUserSession();
            toast.success('Welcome back, Investor!');
            navigate('/investor/dashboard');
            return;
          }
          localStorage.setItem('user_data', JSON.stringify(userPayload));
          activateUserSession();
          toast.success('Welcome back!');
          navigate('/account');
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="py-20 px-4">
        <Card className="max-w-md mx-auto">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              {accountType === 'investor' ? (
                <TrendingUp className="h-8 w-8 text-green-600" />
              ) : (
                <User className="h-8 w-8 text-green-600" />
              )}
            </div>
            <CardTitle className="text-2xl">
              {isLogin ? 'Welcome Back' : `Create ${accountType === 'investor' ? 'Investor' : ''} Account`}
            </CardTitle>
            <p className="text-gray-500 text-sm">
              {isLogin ? 'Sign in to your account' : accountType === 'investor' ? 'Join Daye Farm as an investor' : 'Join Daye Farm today'}
            </p>

            {/* Account Type Selector - only for signup */}
            {!isLogin && (
              <div className="flex gap-2 mt-4 p-1 bg-gray-100 rounded-lg" data-testid="account-type-selector">
                <button
                  type="button"
                  onClick={() => setAccountType('customer')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                    accountType === 'customer' ? 'bg-white text-green-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid="account-type-customer"
                >
                  <ShoppingBag className="h-4 w-4" /> Customer
                </button>
                <button
                  type="button"
                  onClick={() => setAccountType('investor')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                    accountType === 'investor' ? 'bg-white text-green-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid="account-type-investor"
                >
                  <TrendingUp className="h-4 w-4" /> Investor
                </button>
              </div>
            )}
          </CardHeader>

          <CardContent>
            {sessionExpired && (
              <div className="mb-4 border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" role="alert" data-testid="session-expired-alert">
                Your session ended after 10 minutes of inactivity. Please sign in again.
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <AuthFormFields
                isLogin={isLogin}
                formData={formData}
                onChange={handleChange}
                dialCode={dialCode}
                onDialCodeChange={setDialCode}
                showPassword={showPassword}
                onToggleShowPassword={() => setShowPassword((v) => !v)}
              />

              <ConsentRow
                checked={legalConsent}
                onChange={setLegalConsent}
                isLogin={isLogin}
              />

              <Button
                type="submit"
                className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading || !legalConsent}
                data-testid="auth-submit-btn"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {isLogin ? 'Sign In' : accountType === 'investor' ? 'Create Investor Account' : 'Create Account'}
              </Button>

              {isLogin && (
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm text-green-600 hover:text-green-700 font-medium"
                    data-testid="forgot-password-link"
                  >
                    Forgot your password?
                  </button>
                </div>
              )}
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                {isLogin ? "Don't have an account? " : 'Already have an account? '}
                <button
                  type="button"
                  onClick={() => setIsLogin(!isLogin)}
                  className="text-green-600 hover:text-green-700 font-semibold"
                  data-testid="auth-toggle-btn"
                >
                  {isLogin ? 'Sign Up' : 'Sign In'}
                </button>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Auth;
